import { useEffect } from 'react'
import store, { obterChaveCestaProdutosPorAba, QUATRO_DIAS_EM_MS } from 'app/redux/store/store'
import { selecionarProdutosAction } from 'app/redux/produtosSelecionadosSlice'
import { IOrcamento } from 'app/types/IOrcamento'
import { IProduto } from 'app/types/produto/IProduto'
import { abaEstaViva } from 'app/utils/abaEstaViva'
import { localizarOrcamentoEmAndamentoOrfaoDeOutraAba } from './localizarOrcamentoEmAndamentoOrfaoDeOutraAba'

declare var window: any
declare var localStorage: any

// 491123 — cada aba tem sua PRÓPRIA cesta, isolada no localStorage por aba_id (ver
// obterChaveCestaProdutosPorAba em app/redux/store/store.ts) — abrir uma segunda aba não "herda"
// os produtos da primeira. Se o navegador fechar anormalmente (ou o computador for desligado sem
// fechar o navegador) antes do usuário aplicar os produtos de uma aba, a cesta DAQUELA aba fica
// órfã no localStorage (sem nenhuma aba viva para reivindicá-la). Ao entrar num orçamento de
// Venda/Outras Saídas/Transferência — novo ou existente, tanto faz — este hook varre o localStorage
// atrás de cestas de OUTRAS abas, confirma (via o mesmo mecanismo de BroadcastChannel que
// _Base.cshtml já usa para detectar aba duplicada) que a aba dona de cada uma não está mais viva, e
// só então oferece recuperar os produtos para a aba atual. Cestas de abas que ainda respondem são
// deixadas intactas — não é este hook quem decide o destino delas.
//
// Recuperação é UMA CESTA POR VEZ, não todas de uma vez: se houver órfãs de mais de uma aba, só a
// primeira é oferecida (aceita ou recusada, sempre removida do localStorage ao final). As demais
// ficam pendentes para a PRÓXIMA VEZ que o usuário entrar em um orçamento novo — mesma aba ou
// outra, tanto faz — que é quando este hook roda de novo.
//
// A checagem roda uma vez por ORÇAMENTO (flag de módulo por `IdentificadorOrcamento`, não redux):
// o componente que usa este hook (ItensOrcamentoContainer) remonta a cada navegação entre abas do
// MESMO orçamento (Itens, Cliente, Entrega...) — repetir a pergunta nessas remontagens seria
// incômodo, então elas são ignoradas (mesmo `IdentificadorOrcamento` de antes). Já entrar em um
// orçamento DIFERENTE (novo, criado com `oConstrutor`/random id a cada vez — ver APRENDIZADOS #223
// — ou um já existente diferente do último verificado) sempre gera um `IdentificadorOrcamento`
// novo, o que libera a checagem de novo e processa a próxima cesta órfã pendente, se houver.
let algumaVerificacaoCestaJaFoiFeita = false
let identificadorOrcamentoUltimaVerificacaoCesta: string | null = null

const TIPOS_ORCAMENTO_COM_CESTA: IOrcamento["TipoOrcamentoDescricao"][] = ["Venda", "Outras Saídas", "Transferências"]

// 482572 — exportados para uso em useRecuperarOrcamentoEmAndamento.ts: ao recuperar o snapshot de
// orçamento NÃO GRAVADO de uma aba morta, aquela mesma aba pode ter deixado também uma cesta de
// produtos pendente (mesmo abaId) — reaproveita a leitura em vez de duplicar o parsing.
export const PREFIXO_CHAVE_CESTA_LOCALSTORAGE = 'persist:produtosSelecionados_'
// Formato usado antes deste refinamento (cesta única, compartilhada por todas as abas, sem
// sufixo de aba_id) — sem dono para checar vivacidade, então é sempre tratada como órfã. Mantido
// só para não perder dados de uma cesta pendente de antes deste deploy.
const CHAVE_CESTA_LOCALSTORAGE_LEGADA = 'persist:produtosSelecionados'

type CestaPersistidaLida = { produtos: IProduto[], atualizadoEm?: number }

// redux-persist serializa cada campo do slice individualmente (JSON.stringify por campo, não do
// objeto inteiro) — por isso são necessários dois JSON.parse em cascata para chegar em `produtos`/
// `AtualizadoEm` (este último gravado por app/redux/store/store.ts, ver produtosSelecionadosSlice.ts).
export function lerCestaDeChaveLocalStorage(chaveLocalStorage: string): CestaPersistidaLida | null {
    try {
        const bruto = localStorage.getItem(chaveLocalStorage)
        if (!bruto) return null
        const nivel1 = JSON.parse(bruto)
        const produtos = nivel1.produtos ? JSON.parse(nivel1.produtos) : []
        const atualizadoEm = nivel1.AtualizadoEm ? JSON.parse(nivel1.AtualizadoEm) : undefined
        return {
            produtos: Array.isArray(produtos) ? produtos : [],
            atualizadoEm: typeof atualizadoEm === 'number' ? atualizadoEm : undefined
        }
    } catch {
        return null
    }
}

// 482572 — atualizadoEm ausente (cesta de antes deste deploy) NÃO conta como expirada — só expira
// quem realmente tem o timestamp e ele já passou de QUATRO_DIAS_EM_MS.
function cestaExpirou(cesta: CestaPersistidaLida): boolean {
    return typeof cesta.atualizadoEm === 'number' && (Date.now() - cesta.atualizadoEm) > QUATRO_DIAS_EM_MS
}

function obterChavesCestaOutrasAbas(chaveAtual: string): string[] {
    const chaves: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
        const chave = localStorage.key(i)
        if (!chave || chave === chaveAtual) continue
        if (chave === CHAVE_CESTA_LOCALSTORAGE_LEGADA || chave.indexOf(PREFIXO_CHAVE_CESTA_LOCALSTORAGE) === 0) {
            chaves.push(chave)
        }
    }
    return chaves
}

// Apaga as cestas de outras abas que não têm produtos (`"produtos":"[]"`) ou estão ilegíveis: são
// lixo — aba que aplicou os produtos e fechou, ou escrita corrompida — não cesta órfã recuperável.
// Roda SEMPRE que este hook verifica um orçamento, inclusive nos tipos sem cesta (Devolução etc.) e
// quando não há nenhuma órfã de verdade: nada nesta limpeza depende do fluxo atual, e deixá-la atrás
// dessas condições era o que permitia as chaves vazias acumularem no localStorage. Vem antes do
// probe de vivacidade, que custa TIMEOUT_PROBE_ABA_VIVA_MS por chave — chave vazia não merece esse
// custo, e não faz diferença se a aba dona ainda está viva (vazia é descartável de qualquer forma).
// A chave da PRÓPRIA aba nunca entra aqui (obterChavesCestaOutrasAbas exclui `chaveAtual`): é a
// cesta viva, gerenciada pelo redux-persist, que a reescreve a cada mudança.
function removerCestasVaziasDeOutrasAbas(chaveAtual: string) {
    for (const chave of obterChavesCestaOutrasAbas(chaveAtual)) {
        const cesta = lerCestaDeChaveLocalStorage(chave)
        if (!cesta || cesta.produtos.length === 0 || cestaExpirou(cesta)) localStorage.removeItem(chave)
    }
}

type CestaOrfa = { chave: string, produtos: IProduto[], abaId: string | null }

// Varre as cestas de outras abas: descarta silenciosamente as vazias/ilegíveis (lixo), pula as que
// pertencem a uma aba ainda viva (em uso normal em outro lugar) e devolve, das que restaram, uma
// entrada POR ABA (chave + seus próprios produtos) — nunca mescladas num array único, porque a
// recuperação é oferecida uma cesta de cada vez (ver comentário no topo do arquivo).
async function localizarCestasOrfasDeOutrasAbas(chaveAtual: string): Promise<CestaOrfa[]> {
    const cestasOrfas: CestaOrfa[] = []

    for (const chave of obterChavesCestaOutrasAbas(chaveAtual)) {
        const cesta = lerCestaDeChaveLocalStorage(chave)
        if (!cesta || cesta.produtos.length === 0 || cestaExpirou(cesta)) {
            localStorage.removeItem(chave)
            continue
        }

        const abaId = chave === CHAVE_CESTA_LOCALSTORAGE_LEGADA ? null : chave.slice(PREFIXO_CHAVE_CESTA_LOCALSTORAGE.length)
        const viva = abaId ? await abaEstaViva(abaId) : false
        if (viva) continue

        cestasOrfas.push({ chave, produtos: cesta.produtos, abaId })
    }

    return cestasOrfas
}

// 482572 — abaIds cuja cesta órfã NÃO deve ser perguntada aqui: são abas que também deixaram um
// orçamento de Venda não gravado pendente (mesmos critérios do orfaoNaoGravado em
// useRecuperarOrcamentoEmAndamento.ts) — a cesta dessas abas é recuperada automaticamente JUNTO com
// aquele orçamento (ver recuperarCestaDaMesmaAbaAutomaticamente lá), nunca perguntada isoladamente
// aqui. Sem esta exclusão, os dois hooks corriam em paralelo (ambos montados por
// ItensOrcamentoContainer) e a pergunta da cesta podia aparecer ANTES da pergunta do orçamento —
// que é a que realmente importa nesse caso.
async function obterAbaIdsReservadosParaRecuperacaoDeOrcamento(): Promise<Set<string>> {
    const orfaosDeOrcamento = await localizarOrcamentoEmAndamentoOrfaoDeOutraAba()

    return new Set(
        orfaosDeOrcamento
            .filter(o => o.abaId != null && o.orcamento.Numero === 0 && o.orcamento.TipoOrcamento === 0 && (o.orcamento.Itens?.length ?? 0) > 0)
            .map(o => o.abaId as string)
    )
}

// Já verificado para ESTE `IdentificadorOrcamento` (ou não há nenhum orçamento carregado ainda)?
function jaVerificouEsteOrcamento(identificadorOrcamento: string | undefined | null): boolean {
    return algumaVerificacaoCestaJaFoiFeita && identificadorOrcamentoUltimaVerificacaoCesta === (identificadorOrcamento ?? null)
}

function marcarOrcamentoComoVerificado(identificadorOrcamento: string | undefined | null) {
    algumaVerificacaoCestaJaFoiFeita = true
    identificadorOrcamentoUltimaVerificacaoCesta = identificadorOrcamento ?? null
}

export function useRecuperarCestaProdutosAnterior(orcamento: IOrcamento | null) {
    useEffect(() => {
        // orcamento ainda carregando (TipoOrcamentoDescricao chega junto, ver criarOrcamentoAction)
        // — não consome a checagem, tenta novamente quando popular
        if (!orcamento?.TipoOrcamentoDescricao) return
        if (jaVerificouEsteOrcamento(orcamento.IdentificadorOrcamento)) return

        // 491123 — produtosSelecionados tem seu próprio persistReducer (localStorage), independente
        // do persistor/PersistGate raiz (que só espera a rehidratação da árvore com blacklist).
        // A leitura do localStorage é assíncrona (Promise), então na primeira renderização deste
        // container a rehidratação da cesta pode ainda não ter chegado — aguarda via store.subscribe
        // até `_persist.rehydrated` ficar true antes de decidir qualquer coisa (garante também que
        // a própria aba_id desta aba já está estável em sessionStorage).
        const verificarRecuperacao = async () => {
            if (jaVerificouEsteOrcamento(orcamento.IdentificadorOrcamento)) return
            const cesta = store.getState().produtosSelecionados
            if (!cesta._persist?.rehydrated) return

            marcarOrcamentoComoVerificado(orcamento.IdentificadorOrcamento)

            const chaveAtual = 'persist:' + obterChaveCestaProdutosPorAba()
            removerCestasVaziasDeOutrasAbas(chaveAtual)

            if (!TIPOS_ORCAMENTO_COM_CESTA.includes(orcamento.TipoOrcamentoDescricao)) return

            const cestasOrfas = await localizarCestasOrfasDeOutrasAbas(chaveAtual)
            if (cestasOrfas.length === 0) return

            const abaIdsReservados = await obterAbaIdsReservadosParaRecuperacaoDeOrcamento()
            const cestasOrfasDisponiveis = cestasOrfas.filter(c => c.abaId == null || !abaIdsReservados.has(c.abaId))
            if (cestasOrfasDisponiveis.length === 0) return

            // Uma cesta por vez: só a primeira órfã encontrada é oferecida agora (aceita ou
            // recusada, sempre removida ao final). As demais ficam no localStorage e serão
            // oferecidas na próxima vez que este hook rodar para um orçamento diferente — ou seja,
            // na próxima entrada em um orçamento novo (mesma aba ou outra).
            const proximaCesta = cestasOrfasDisponiveis[0]
            const quantidadeItens = proximaCesta.produtos.length
            const mensagem = `Existe uma cesta de produtos anterior com ${quantidadeItens} ${quantidadeItens === 1 ? "item" : "itens"} que ainda não foi incluída em um orçamento.\n\nDeseja recuperá-la?`

            if (window.confirm(mensagem)) {
                store.dispatch(selecionarProdutosAction(proximaCesta.produtos))
                localStorage.removeItem(proximaCesta.chave)
                // 491123 — dispatch direto de abrirModalCestaProdutosAction não basta: o Dialog só
                // existe dentro de PaginaPesquisaProdutoContainer, que ainda não está montado aqui
                // (o usuário acabou de entrar em Itens). window.abrirModalCestaProdutosSemNavegar
                // (definido em _BaseInterno.cshtml) monta o container no host oculto e dispatcha a
                // action — mesmo mecanismo já usado pelo botão flutuante e pelo bloqueio de
                // "Finalizar" com produtos pendentes na cesta (ver orcamentoController.tsx).
                window.abrirModalCestaProdutosSemNavegar && window.abrirModalCestaProdutosSemNavegar()
            } else {
                localStorage.removeItem(proximaCesta.chave)
            }
        }

        verificarRecuperacao()
        if (jaVerificouEsteOrcamento(orcamento.IdentificadorOrcamento)) return

        const unsubscribe = store.subscribe(() => {
            verificarRecuperacao()
            if (jaVerificouEsteOrcamento(orcamento.IdentificadorOrcamento)) unsubscribe()
        })
        return unsubscribe
    }, [orcamento?.TipoOrcamentoDescricao, orcamento?.IdentificadorOrcamento])
}
